<template>
  <Menu as="div" class="relative shrink-0">
    <MenuButton as="template">
      <button
        ref="buttonRef"
        v-tooltip="'Change status'"
        type="button"
        aria-label="Change status"
        aria-haspopup="menu"
        class="mt-0.5 inline-flex size-5 cursor-pointer items-center justify-center focus:outline-none"
      >
        <span
          class="size-3.5 rounded-full border-2 transition-colors hover:scale-110"
          :class="circleClass"
        />
      </button>
    </MenuButton>
    <Teleport to="body">
      <MenuItems as="template">
        <div
          ref="floatingRef"
          :style="floatingStyles"
          class="z-50 min-w-36 rounded-md bg-white shadow-lg ring-1 ring-gray-400 focus:outline-none"
        >
          <MenuItem
            v-for="option in options"
            :key="option.title"
            v-slot="{ active }"
          >
            <button
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm first:rounded-t-md last:rounded-b-md"
              :class="active ? 'bg-gray-100' : ''"
              @click="option.action()"
            >
              <Icon
                v-if="option.selected"
                name="check"
                class="size-3.5 text-gray-500"
              />
              <span :class="option.selected ? 'font-semibold' : ''">
                {{ option.title }}
              </span>
            </button>
          </MenuItem>
        </div>
      </MenuItems>
    </Teleport>
  </Menu>
</template>

<script setup lang="ts">
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/vue";
import { useFloating, flip, shift, offset, autoUpdate } from "@floating-ui/vue";
import { PropType, ref } from "vue";
import Icon from "@/components/Core/Icon.vue";

interface MenuOption {
  title: string;
  // Optional so callers (e.g. MyTasksSlideoverPanel.statusMenuItems) can
  // omit it for items that aren't a "current state" indicator.
  selected?: boolean;
  action: () => void;
}

defineProps({
  options: { type: Array as PropType<MenuOption[]>, required: true },
  circleClass: { type: String, default: "" },
});

// Teleporting MenuItems to <body> escapes the slideover's
// `overflow-y-auto` parent so the dropdown isn't clipped near the bottom.
// Floating UI handles positioning relative to the trigger and flips
// upward when there isn't room below. `as="template"` on the Menu*
// components renders the underlying native elements directly so we can
// take HTMLElement refs without poking at undocumented `.el` internals.
const buttonRef = ref<HTMLElement | null>(null);
const floatingRef = ref<HTMLElement | null>(null);

const { floatingStyles } = useFloating(buttonRef, floatingRef, {
  placement: "bottom-start",
  middleware: [offset(4), flip(), shift({ padding: 8 })],
  whileElementsMounted: autoUpdate,
});
</script>
